/** The results shell (UX-SPEC §3.1): header, sticky headline and heat strip, view navigation, main view. */
import { signal } from '@preact/signals';
import { useLayoutEffect } from 'preact/hooks';
import { figuresFor } from '../../shared/model-figures.ts';
import type { ExportFormat } from '../../shared/protocol.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { BenchView } from '../bench/BenchView.tsx';
import { BudgetDialog } from '../bench/BudgetDialog.tsx';
import { CostDialog } from '../bench/CostDialog.tsx';
import { JobsIndicator } from '../bench/JobsIndicator.tsx';
import { KeyDialog } from '../bench/KeyDialog.tsx';
import { KeyStatus } from '../bench/KeyStatus.tsx';
import { MarkdownReview, openMarkdownReview } from '../bench/MarkdownReview.tsx';
import { RewriteOverviewDialog } from '../bench/RewriteOverview.tsx';
import { RunSummaryDialog } from '../bench/RunSummaryDialog.tsx';
import { VersionBar } from '../bench/VersionBar.tsx';
import { origin } from '../bench/chain-control.ts';
import { declinedItems, lenientAppendix, withGuardrailsNote } from '../bench/rewrite-export.ts';
import type { RewriteExportFormat } from '../bench/rewrite-export.ts';
import {
	chain,
	exportAppendix,
	lastReport,
	latestN,
	lens,
	lensJobId,
	revertedUnits,
	rewrittenText,
} from '../bench/state.ts';
import { downloadVersion } from '../bench/version-export.ts';
import { exportAs, newAnalysis } from '../controller.ts';
import { fmtInt } from '../format.ts';
import { hashFor, route } from '../router.ts';
import { notices } from '../state.ts';
import { REPORT_EXPORT, REVIEW, REWRITE_EXPORT, VERSIONS } from '../strings-bench.ts';
import { APP_LABEL } from '../strings-explain.ts';
import { HELP } from '../strings-help.ts';
import { APP, EXPORT_ITEMS, FOOT, HEADER, NAV, WORKING } from '../strings.ts';
import { MenuButton, Notice } from '../ui.tsx';
import { APP_VERSION } from '../version.ts';
import { ChannelDetailView } from '../views/ChannelDetail.tsx';
import { ChannelsView } from '../views/Channels.tsx';
import { Diagnostics } from '../views/Diagnostics.tsx';
import { Glossary } from '../views/Glossary.tsx';
import { HotspotsView } from '../views/Hotspots.tsx';
import { Overview } from '../views/Overview.tsx';
import { Reader } from '../views/Reader.tsx';
import { beginWork, endWork, nextPaint } from '../work.ts';
import { HeatStrip } from './HeatStrip.tsx';
import { HelpMenu } from './HelpMenu.tsx';
import { WorkStatus } from './Working.tsx';

const busy = signal<Partial<Record<ExportFormat, boolean>>>({});
const exportError = signal<string | null>(null);

async function doExport(format: ExportFormat): Promise<void> {
	busy.value = { ...busy.value, [format]: true };
	exportError.value = null;
	// no basis for an estimate (no measured rate for an export): the working indicator alone
	const id = `export-${format}`;
	beginWork(id, WORKING.ops.export(WORKING.formats[format]));
	try {
		await exportAs(format);
	} catch (e) {
		exportError.value = HEADER.exportFailed((e as Error).message);
	} finally {
		endWork(id);
		busy.value = { ...busy.value, [format]: false };
	}
}

const rewriteBusy = signal<Partial<Record<RewriteExportFormat, boolean>>>({});

async function doRewriteExport(vm: ViewModel, format: RewriteExportFormat): Promise<void> {
	const text = rewrittenText.value;
	if (!text) return;
	rewriteBusy.value = { ...rewriteBusy.value, [format]: true };
	exportError.value = null;
	const id = `rewrite-export-${format}`;
	beginWork(id, WORKING.ops.rewriteExport(WORKING.formats[format]));
	try {
		await nextPaint();
		const kept = exportAppendix.value
			? lenientAppendix(lens.value, revertedUnits.value)
			: undefined;
		// a version made with the editorial guardrails says so in one line (ED-191)
		const made = chain.value?.versions.at(-1)?.made;
		const guarded = made?.kind === 'job' && made.guardrails === true;
		// the passages the editor left as they are, when this version's run is the one the lens shows (GR2)
		const declined = guarded && made.jobId === lensJobId.value ? declinedItems(lens.value) : [];
		const appendix = withGuardrailsNote(kept, guarded, declined);
		// the Markdown is the latest version with the upload's structure (UD-30)
		const label = VERSIONS.version(String(latestN.value));
		await downloadVersion(vm.meta, text, format, 'rewrite', label, appendix);
	} catch (e) {
		exportError.value = HEADER.exportFailed((e as Error).message);
	} finally {
		endWork(id);
		rewriteBusy.value = { ...rewriteBusy.value, [format]: false };
	}
}

const reportBusy = signal(false);

/** Downloads the rewrite report of the run the lens shows (the work item is registered inside). */
async function doReportExport(vm: ViewModel): Promise<void> {
	reportBusy.value = true;
	exportError.value = null;
	try {
		// the download module is loaded on first use: it and the builder stay out of the initial bundle (CD-131)
		const { downloadRewriteReport } = await import('../bench/report-export.ts');
		await downloadRewriteReport(vm);
	} catch (e) {
		exportError.value = REPORT_EXPORT.failed((e as Error).message);
	} finally {
		reportBusy.value = false;
	}
}

const ORIGINAL_FORMATS = ['txt', 'md', 'docx'] as const;
const originalBusy = signal<Partial<Record<RewriteExportFormat, boolean>>>({});

/** Exports version 0's text (UD-27): the upload as analysed, with no appendix. */
async function doOriginalExport(vm: ViewModel, format: RewriteExportFormat): Promise<void> {
	const o = origin();
	if (!o) return;
	originalBusy.value = { ...originalBusy.value, [format]: true };
	exportError.value = null;
	const id = `original-export-${format}`;
	beginWork(id, WORKING.ops.rewriteExport(WORKING.formats[format]));
	try {
		await nextPaint();
		await downloadVersion(vm.meta, o.text, format, 'original', VERSIONS.original);
	} catch (e) {
		exportError.value = HEADER.exportFailed((e as Error).message);
	} finally {
		endWork(id);
		originalBusy.value = { ...originalBusy.value, [format]: false };
	}
}

const NAV_ITEMS = [
	{ view: 'overview', label: NAV.overview },
	{ view: 'read', label: NAV.reader },
	{ view: 'channels', label: NAV.channels },
	{ view: 'hotspots', label: NAV.hotspots },
	{ view: 'diagnostics', label: NAV.diagnostics },
	{ view: 'bench', label: NAV.bench },
] as const;

function ViewNav({ bottom }: { bottom?: boolean }) {
	const current = route.value.view === 'channel' ? 'channels' : route.value.view;
	// UX-MOB-02 fixes the bottom bar at five items; Bench (W5c) stays a top-nav-only, desktop entry point (BENCH-UX-SPEC)
	const items = bottom ? NAV_ITEMS.filter((it) => it.view !== 'bench') : NAV_ITEMS;
	return (
		<nav
			class={bottom ? 'bottomnav' : 'viewnav'}
			aria-label={bottom ? `${NAV.views} (bottom)` : NAV.views}
			data-testid={bottom ? 'bottom-nav' : 'view-nav'}
		>
			{items.map((it) => (
				<a
					key={it.view}
					class={`viewtab${it.view === current ? ' is-current' : ''}`}
					href={hashFor(it.view)}
					aria-current={it.view === current ? 'page' : undefined}
				>
					{it.label}
				</a>
			))}
		</nav>
	);
}

function Main({ vm }: { vm: ViewModel }) {
	const r = route.value;
	switch (r.view) {
		case 'read':
			return <Reader vm={vm} />;
		case 'channels':
			return <ChannelsView vm={vm} />;
		case 'channel':
			return <ChannelDetailView vm={vm} id={r.id} />;
		case 'hotspots':
			return <HotspotsView vm={vm} />;
		case 'diagnostics':
			return <Diagnostics vm={vm} />;
		case 'bench':
			return <BenchView vm={vm} />;
		case 'glossary':
			return <Glossary />;
		default:
			return <Overview vm={vm} />;
	}
}

export function Shell({ vm }: { vm: ViewModel }) {
	const e = vm.ensemble;
	// the fixed speech caveat of the analysed result's model (null removes it)
	const speech = figuresFor(e.model.id).speechCaveat;
	const view = route.value.view;
	const channelId = route.value.id;
	// a new view starts at the top; the reader keeps its own position (UX-MAP-04)
	// biome-ignore lint/correctness/useExhaustiveDependencies: scroll only when the view or channel changes
	useLayoutEffect(() => {
		if (view !== 'read') window.scrollTo(0, 0);
	}, [view, channelId]);
	return (
		<div class="shell" data-testid="shell" data-view={route.value.view}>
			<a class="skip" href="#main">
				{APP.skip}
			</a>
			<div class="stickytop">
				<header class="top">
					<div class="brand">
						{APP.name}
						<span class="brand-version" data-testid="app-version">
							{APP_LABEL(APP_VERSION)}
						</span>
					</div>
					<div class="docname" data-testid="docname">
						<span class="mono">{vm.meta.name}</span>
						{vm.meta.version ? (
							<span data-testid="docname-version">
								{' '}
								· {VERSIONS.version(String(vm.meta.version.n))}
							</span>
						) : null}{' '}
						· {HEADER.words(fmtInt(vm.meta.words))}
					</div>
					<div class="top-actions">
						<button type="button" class="btn btn-secondary" onClick={newAnalysis}>
							{HEADER.newAnalysis}
						</button>
						<HelpMenu vm={vm} />
						<MenuButton
							label={HEADER.export}
							items={[
								...(Object.keys(EXPORT_ITEMS) as ExportFormat[]).map((f) => ({
									id: f,
									label: EXPORT_ITEMS[f],
									busy: !!busy.value[f],
									onSelect: () => void doExport(f),
								})),
								...(rewrittenText.value
									? (Object.keys(REWRITE_EXPORT) as RewriteExportFormat[]).map((f) => ({
											id: `rewrite-${f}`,
											label: REWRITE_EXPORT[f],
											busy: !!rewriteBusy.value[f],
											onSelect: () => void doRewriteExport(vm, f),
										}))
									: []),
								...(lens.value && lastReport.value
									? [
											{
												id: 'rewrite-report',
												label: REPORT_EXPORT.menu,
												busy: reportBusy.value,
												onSelect: () => void doReportExport(vm),
											},
										]
									: []),
								...((chain.value?.versions.length ?? 0) > 0
									? [
											{
												id: 'review-markdown',
												label: REVIEW.open,
												onSelect: () => void openMarkdownReview(chain.value?.versions.length ?? 0),
											},
										]
									: []),
								...((chain.value?.versions.length ?? 0) > 0
									? ORIGINAL_FORMATS.map((f) => ({
											id: `original-${f}`,
											label: VERSIONS.exportOriginal[f],
											busy: !!originalBusy.value[f],
											onSelect: () => void doOriginalExport(vm, f),
										}))
									: []),
							]}
						/>
						<span class="bench-desktop-only">
							<JobsIndicator />
						</span>
						<KeyStatus />
					</div>
				</header>
				<div class="sticky" data-testid="sticky">
					<div class="compact">
						<span class="compact-label" data-testid="compact-headline">
							{e.headline}
						</span>
						{/* one line under the headline: the caveat, then the speech caveat (CD-66), so the sticky header does not grow */}
						<span class="compact-caveat">
							<span data-testid="compact-caveat">{e.caveat}</span>{' '}
							{speech ? (
								<span data-testid="compact-speech-caveat">
									{speech.short}{' '}
									<a href={HELP.limitationsHref} target="_blank" rel="noreferrer noopener">
										{speech.link}
									</a>
									.
								</span>
							) : null}
						</span>
						<details class="compact-caveat-m" data-testid="compact-caveat-m">
							<summary>{FOOT.caveatLabel}</summary>
							{e.caveat}{' '}
							{speech ? (
								<span data-testid="compact-speech-caveat-m">
									{speech.short}{' '}
									<a href={HELP.limitationsHref} target="_blank" rel="noreferrer noopener">
										{speech.link}
									</a>
									.
								</span>
							) : null}
						</details>
					</div>
					<HeatStrip vm={vm} variant="sticky" />
					<ViewNav />
				</div>
			</div>
			{notices.value.length > 0 || exportError.value ? (
				<output class="notice-bar">
					{notices.value.map((n) => (
						<Notice kind="info" key={n}>
							{n}
						</Notice>
					))}
					{exportError.value ? <Notice kind="error">{exportError.value}</Notice> : null}
				</output>
			) : null}
			<VersionBar />
			<MarkdownReview />
			<main id="main" tabIndex={-1}>
				<Main vm={vm} />
			</main>
			<ViewNav bottom />
			<KeyDialog />
			<CostDialog />
			<BudgetDialog />
			<RunSummaryDialog vm={vm} />
			<RewriteOverviewDialog />
			<WorkStatus />
		</div>
	);
}
